import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { RowSelectionProvider } from "@/components/data-table/context/RowSelectionProvider";
import { useRowSelectionContext } from "@/components/data-table/context/RowSelectionContext";
import Form from "@/components/Form";
import { List } from "@/components/list/List";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DEFAULT_DATE_FORMAT } from "@/constants";
import { useFormAutoReset } from "@/hooks/useFormAutoReset";
import { FilterType } from "@/lib/filters";
import { ProjectSchedulesAddSchedulesDialogForm } from "@/modules/projects/components/schedules/ProjectSchedulesAddSchedulesDialogForm";
import { ProjectSchedulesAddSchedulesSubmitButton } from "@/modules/projects/components/schedules/ProjectSchedulesAddSchedulesSubmitButton";
import { useProjectSchedulesContext } from "@/modules/projects/components/schedules/ProjectSchedulesContext";
import { useProjectStoresQuery } from "@/modules/projects/hooks/useProjectStoresQuery";
import { ProjectSchedulesService } from "@/modules/projects/service/projectSchedule";
import { writeProjectSchedulesFormWithoutStores } from "@/schemas/projectSchedule";
import { displayProjectStore } from "@/schemas/projectStore";
import {
  CreateProjectSchedule,
  WriteProjectSchedulesFormWithoutStores,
} from "@/types/projectSchedule";
import { DisplayProjectStore } from "@/types/projectStore";
import { memo, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { format, startOfMonth, endOfMonth, parse } from "date-fns";
import { convertCreateProjectScheduleFormToCreateProjectSchedule } from "./schema";
import {
  AsyncOperationProgress,
  OperationProgressStatus,
} from "@/components/progress/AsyncOperationProgress";
import { useProjectSchedulesUpsertFormWithBatch } from "@/modules/projects/hooks/useProjectSchedulesUpsertFormWithBatch";

const additionalDefaultValues = {
  project_role_id: "",
};

const currentMonthStart = startOfMonth(new Date());
const currentMonthEnd = endOfMonth(new Date());

const ProjectSchedulesAddSchedulesDialogContent = () => {
  const { t } = useTranslation();
  const { store } = useRowSelectionContext();
  const { isAddDialogOpened, setIsAddDialogOpened, refetch, projectId } =
    useProjectSchedulesContext();

  const { query, options } = useProjectStoresQuery({
    projectId,
    enabled: isAddDialogOpened,
    filters: [
      {
        id: "project_id",
        value: {
          value: projectId,
          filter: FilterType.IncludesString,
        },
      },
    ],
  });

  const prepareData = useCallback(
    (values: WriteProjectSchedulesFormWithoutStores) => {
      const { project_role_id } = values;
      const selectedStoreIds = Array.from(store.getSelection());

      return selectedStoreIds
        .map((storeId) =>
          convertCreateProjectScheduleFormToCreateProjectSchedule.parse({
            ...values,
            store_id: storeId,
            project_role_id,
          }),
        )
        .filter(Boolean) as CreateProjectSchedule[];
    },
    [store],
  );

  const processBatch = useCallback(
    (batch: CreateProjectSchedule[]) => {
      return ProjectSchedulesService.createSchedules(projectId, batch);
    },
    [projectId],
  );

  const onSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const { form, defaultValues, mutation, operationProgress, handlers } =
    useProjectSchedulesUpsertFormWithBatch({
      startDate: currentMonthStart,
      endDate: currentMonthEnd,
      schema: writeProjectSchedulesFormWithoutStores,
      additionalDefaultValues,
      prepareData,
      processBatch,
      onSuccess,
    });

  useFormAutoReset({
    form,
    defaultValues: defaultValues as WriteProjectSchedulesFormWithoutStores,
    shouldReset: isAddDialogOpened,
    hasData: true,
  });

  const dateStart = form.watch("date_start");

  useEffect(() => {
    if (dateStart) {
      const parsedDate = parse(dateStart, DEFAULT_DATE_FORMAT, new Date());
      const lastDayOfMonth = endOfMonth(parsedDate);
      form.setValue("date_end", format(lastDayOfMonth, DEFAULT_DATE_FORMAT));
    }
  }, [dateStart, form]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open && operationProgress.isBusy()) {
        // Prevent closing dialog while operation is in progress
        return;
      }
      form.reset(defaultValues);
      operationProgress.reset();
      store.clear();
      setIsAddDialogOpened(open);
    },
    [defaultValues, form, setIsAddDialogOpened, operationProgress, store],
  );

  const handleClose = useCallback(() => {
    setIsAddDialogOpened(false);
  }, [setIsAddDialogOpened]);

  const handleSubmit = useCallback(
    async (values: WriteProjectSchedulesFormWithoutStores) => {
      await mutation.mutateAsync(values);
    },
    [mutation],
  );

  const isShowingProgress =
    operationProgress.status !== OperationProgressStatus.IDLE;

  const tableFeatures = useMemo(
    () => ({
      enableRowSelection: true,
      enableSelectAll: true,
    }),
    [],
  );

  return (
    <Dialog open={isAddDialogOpened} onOpenChange={onOpenChange}>
      {isShowingProgress ? (
        <>
          <DialogContent className="max-w-[40dvw] min-w-[700px] max-h-[90dvh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-medium text-gray-800">
                {t("projectSchedules.dialog.addNew")}
              </DialogTitle>
            </DialogHeader>
            <AsyncOperationProgress
              operationProgress={operationProgress}
              currentItemLabel={operationProgress.currentItemLabel}
              translationKey="projectSchedules.progress"
              onCancel={handlers.cancel}
              onClose={handleClose}
            />
          </DialogContent>
        </>
      ) : (
        <DialogContent className="max-w-[90dvw] h-[95dvh]">
          <DataTableQueryContextProvider value={{ query }}>
            <List<DisplayProjectStore>
              schema={displayProjectStore}
              name="projectStores"
              query={query}
              options={options}
              tableFeatures={tableFeatures}
            >
              <Form form={form} className="min-w-0" handleSubmit={handleSubmit}>
                <DialogHeader>
                  <DialogTitle className="text-xl font-medium text-gray-800">
                    {t("projectSchedules.dialog.addNew")}
                  </DialogTitle>
                </DialogHeader>
                <div className="flex flex-row items-center gap-2">
                  <List.Menubar className="m-0 grow">
                    <List.Menubar.Tools inDialog />
                    <List.Menubar.Columns inDialog />
                    <List.Menubar.Reset inDialog />
                  </List.Menubar>
                </div>
                <div className="flex flex-row grow gap-2 overflow-hidden">
                  <List.Content
                    className="overflow-x-auto flex-grow"
                    isInDialog
                  >
                    <List.Status>
                      <List.SelectedRowsStatus />
                    </List.Status>
                  </List.Content>
                  <Card className="min-w-[600px] overflow-y-auto">
                    <CardHeader>
                      <CardTitle>
                        {t("projectSchedules.dialog.formTitle")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-2 ">
                      <div className="grid gap-4">
                        <ProjectSchedulesAddSchedulesDialogForm />
                      </div>
                    </CardContent>
                  </Card>
                </div>
                <DialogFooter className="gap-3 ">
                  <DialogClose asChild>
                    <Button variant="link">
                      {t("projectSchedules.dialog.cancel")}
                    </Button>
                  </DialogClose>
                  <ProjectSchedulesAddSchedulesSubmitButton />
                </DialogFooter>
              </Form>
            </List>
          </DataTableQueryContextProvider>
        </DialogContent>
      )}
    </Dialog>
  );
};

const ProjectSchedulesAddSchedulesDialogComponent = () => {
  return (
    <RowSelectionProvider>
      <ProjectSchedulesAddSchedulesDialogContent />
    </RowSelectionProvider>
  );
};
export const ProjectSchedulesAddSchedulesDialog = memo(
  ProjectSchedulesAddSchedulesDialogComponent,
);
